/**
 * Watch a clip here, rather than being handed a link to somewhere else.
 *
 * The portal used to `window.open` a presigned URL. That is a link, not a
 * review tool: it loses the row you were looking at, it opens a bare video on a
 * blank tab with none of the provenance beside it, and popup blockers eat it.
 * Since there is no video moderation anywhere in this system — a deliberate
 * decision on evidence, 20% false positives on known-good clips — a human
 * actually looking at the output is the only control that exists. It should
 * take one click and keep the context.
 *
 * Presigning is still lazy and per-asset. A table of 200 clips must not mint 200
 * signed URLs to render, so nothing is signed until someone asks to watch.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, getClipUrl } from '../api';
import { message } from './async';
import { Failed } from './ui';

type Signed = { url: string; contentType?: string; key?: string; expiresAt: number };

export function InlinePlayer({
  assetId,
  storageKey,
  /** A URL the caller already has (asset detail returns one). Skips the extra
   *  round trip; everything else about the component is unchanged. */
  url,
  contentType,
  familyScoped,
}: {
  assetId: string;
  storageKey?: string;
  url?: string;
  contentType?: string;
  familyScoped?: boolean;
}) {
  const [signed, setSigned] = useState<Signed | null>(
    url ? { url, ...(contentType ? { contentType } : {}), expiresAt: Date.now() + 900_000 } : null,
  );
  const [busy, setBusy] = useState(false);
  /**
   * WITH the cause, so the triage can run. This was a bare string in a red
   * span, and api.ts falls back to the status when the body has no `error` —
   * so a whole error message could be the characters "404", printed beside a
   * button, explaining nothing. It also meant the two answers that are not
   * faults at all were rendered as though they were: a `staff` role removed
   * mid-session, and a deployment whose clip-signing route refuses permanently.
   */
  const [failure, setFailure] = useState<{ error: string; cause: unknown } | null>(null);
  const [refused, setRefused] = useState(false);

  const sign = useCallback(async () => {
    setBusy(true);
    setFailure(null);
    try {
      const out = await getClipUrl(assetId);
      setSigned({
        url: out.url,
        contentType: out.contentType,
        key: out.key,
        expiresAt: Date.now() + (out.expiresInSeconds || 900) * 1000,
      });
    } catch (err) {
      // 403 here is the control plane working as designed, not a fault: family
      // art is somebody's private keepsake and the API will not sign it. It
      // deserves an explanation, not a red error.
      if (err instanceof ApiError && err.status === 403) setRefused(true);
      else setFailure({ error: message(err), cause: err });
    } finally {
      setBusy(false);
    }
  }, [assetId]);

  if (familyScoped || refused) {
    return (
      <div className="notice">
        <b>Not viewable from the control plane.</b>
        <span className="muted small">
          This asset is scoped to one family. `/admin/clips/{'{id}'}/url` refuses to
          sign family-scoped art on purpose — a staff panel that can open a
          child's pictures is a different product. Playback stays on the family
          surface, behind the parent's own credentials.
        </span>
      </div>
    );
  }

  if (!signed) {
    return (
      <div className="player">
        <button onClick={() => void sign()} disabled={busy}>
          {busy ? 'signing a link…' : 'Watch it here'}
        </button>
        {failure && (
          // No `retry`: the button above IS the retry and it is still on
          // screen, so a second one would just be two ways to make the same
          // call from a box that appeared because it failed.
          <Failed
            what="a signed link"
            error={failure.error}
            cause={failure.cause}
            missing="This build of the staff API cannot sign links — it has no /admin/clips/<id>/url route."
          />
        )}
        <span className="muted small">
          Nothing is signed until you ask. The link that appears lasts 15 minutes
          and the API records which operator asked for it.
        </span>
      </div>
    );
  }

  return <Surface signed={signed} storageKey={storageKey ?? signed.key} onRefresh={() => void sign()} />;
}

function Surface({
  signed,
  storageKey,
  onRefresh,
}: {
  signed: Signed;
  storageKey?: string;
  onRefresh: () => void;
}) {
  const kind = classify(signed.contentType, storageKey ?? signed.url);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    setExpired(false);
    const left = signed.expiresAt - Date.now();
    if (left <= 0) {
      setExpired(true);
      return;
    }
    const timer = setTimeout(() => setExpired(true), left);
    return () => clearTimeout(timer);
  }, [signed.expiresAt]);

  return (
    <div className="player">
      {kind === 'video' && (
        // preload="metadata" not "auto": a review screen may hold several of
        // these, and pulling whole MP4s nobody pressed play on costs real
        // egress for no benefit.
        <video src={signed.url} controls preload="metadata" playsInline />
      )}
      {kind === 'image' && <img src={signed.url} alt="asset" />}
      {kind === 'audio' && <audio src={signed.url} controls preload="metadata" />}
      {kind === 'hls' && (
        <div className="notice notice-warn">
          <b>HLS playlist — plays in Safari, not in Chrome or Firefox.</b>
          <span className="muted small">
            Native HLS is Safari-only, and this portal does not bundle hls.js.
            Not built yet. Open it in Safari, or watch the individual segment
            assets below, which are plain MP4.
          </span>
        </div>
      )}
      {kind === 'other' && (
        <div className="notice stub">
          <b>No inline player for this type.</b>
          <span className="muted small">
            {signed.contentType || storageKey || 'unknown type'} — the link below still
            works.
          </span>
        </div>
      )}
      <div className="toolbar" style={{ margin: 0 }}>
        {expired ? (
          <>
            <span className="warn small">The signed link has expired.</span>
            <button onClick={onRefresh}>Sign a fresh one</button>
          </>
        ) : (
          <span className="muted small">Signed link, valid ~15 minutes.</span>
        )}
        <span className="spacer" />
        <a className="small" href={signed.url} target="_blank" rel="noopener noreferrer">
          open in a tab
        </a>
      </div>
      {storageKey && <code className="small muted">{storageKey}</code>}
    </div>
  );
}

function classify(contentType?: string, keyOrUrl?: string): 'video' | 'image' | 'audio' | 'hls' | 'other' {
  const path = (keyOrUrl ?? '').split('?')[0]?.toLowerCase() ?? '';
  if (path.endsWith('.m3u8') || contentType === 'application/x-mpegurl') return 'hls';
  if (contentType?.startsWith('video/') || /\.(mp4|mov|webm)$/.test(path)) return 'video';
  if (contentType?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/.test(path)) return 'image';
  if (contentType?.startsWith('audio/') || /\.(mp3|wav|m4a)$/.test(path)) return 'audio';
  return 'other';
}
